import type { FeatureLimits } from '@/types/api';

/**
 * Converting between the API's feature map and the editor's rows.
 *
 * Separated from the component purely so the file exports one thing — but the split is
 * welcome anyway: these are the parts worth reasoning about on their own, and the parts a
 * mistake in would be silent.
 */

export type FeatureKind = 'quota' | 'unlimited' | 'boolean';

export interface FeatureRow {
  key: string;
  kind: FeatureKind;
  /** Only meaningful when kind is 'quota'. Held as a string so a half-typed value survives. */
  quota: string;
  enabled: boolean;
}

/**
 * Entitlement keys plans carry. Offered first so they are not mistyped.
 *
 * `servers`, `monitors`, `team_members` and `backup_sources` are quotas the API enforces;
 * `api_access` gates API tokens and `backup_downloads` gates pulling an archive through the
 * platform. A key that has to be typed from memory is a key that gets typed wrongly: a live plan
 * reached production with `team_members` set to the boolean `false` where the limit should have
 * been the number 1, which reads as "this plan grants no people at all".
 *
 * `backups` was here until 2026-09-23 as a key that was shown and never enforced. It is gone:
 * `backup_sources` is the count the platform actually applies when a source is added.
 *
 * `command_runs` is different in kind: an allowance per subscription period rather than a count of
 * things that exist, and the only key where leaving it out means no limit rather than none, because
 * the Command Runner was unmetered on every plan before it existed. The free trial sets it to 25.
 *
 * `applications`, `domains` and `firewall` belonged to hosting features the platform no longer has,
 * and were removed from every plan when those went.
 */
export const KNOWN_FEATURE_KEYS = [
  'servers',
  'monitors',
  'backup_sources',
  'team_members',
  'command_runs',
  'backup_downloads',
  'api_access',
];

export function toFeatureRows(features: FeatureLimits): FeatureRow[] {
  return Object.entries(features).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return { key, kind: 'boolean', quota: '', enabled: value };
    }

    return value === -1
      ? { key, kind: 'unlimited', quota: '', enabled: true }
      : { key, kind: 'quota', quota: String(value), enabled: true };
  });
}

/**
 * Rows back to the API's shape.
 *
 * Rows with a blank key are dropped rather than sent as `""`, and a quota that is not a
 * number is dropped too — an unparseable quota must not silently become zero, which would
 * publish a plan granting nothing while looking correct.
 */
export function fromFeatureRows(rows: FeatureRow[]): FeatureLimits {
  const features: FeatureLimits = {};

  for (const row of rows) {
    const key = row.key.trim();

    if (key === '') {
      continue;
    }

    if (row.kind === 'boolean') {
      features[key] = row.enabled;
      continue;
    }

    if (row.kind === 'unlimited') {
      features[key] = -1;
      continue;
    }

    const quota = Number.parseInt(row.quota, 10);

    if (Number.isFinite(quota) && quota >= 0) {
      features[key] = quota;
    }
  }

  return features;
}

/** Rows a person has started but not finished — surfaced rather than silently dropped. */
export function incompleteRows(rows: FeatureRow[]): string[] {
  return rows
    .filter(
      (row) =>
        row.key.trim() !== ''
        && row.kind === 'quota'
        && !(Number.isFinite(Number.parseInt(row.quota, 10)) && Number.parseInt(row.quota, 10) >= 0),
    )
    .map((row) => row.key.trim());
}
